<script>
import baseMixin from '@/mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import { isNonEmptyArray } from '@/helpers/array-helpers'
import dayjs from '@/services/date/index'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import { throttle } from 'lodash'
import SkeletonClientGroupOverviewContainer from '../skeleton/skeleton-client-group-overview-container.vue'
import ClientGroupOverviewGroupSelector from './client-group-overview-group-selector.vue'
import ClientGroupOverviewSchoolMultiSelector from './client-group-overview-school-multi-selector.vue'
import ClientGroupOverviewSchoolsTable from './client-group-overview-schools-table.vue'
import ClientGroupOverviewSchoolsMap from './client-group-overview-schools-map.vue'
import ClientGroupOverviewStatisticsContainer from './client-group-overview-statistics-container.vue'
import ClientGroupOverviewDateFilter from './client-group-overview-date-filter.vue'
import ClientGroupOverviewFailureBlock from './client-group-overview-failure-block.vue'

export default {
  name: 'ClientGroupOverviewContainer',
  components: {
    ClientGroupOverviewGroupSelector,
    ClientGroupOverviewSchoolMultiSelector,
    ClientGroupOverviewSchoolsTable,
    ClientGroupOverviewSchoolsMap,
    ClientGroupOverviewStatisticsContainer,
    ClientGroupOverviewDateFilter,
    SkeletonClientGroupOverviewContainer,
    ClientGroupOverviewFailureBlock,
  },
  mixins: [baseMixin],
  data: () => {
    return {
      selectedGroupId: null,
      selectedSchools: [],
      filterDate: null,
      overviewData: [],
      schoolToFocusOn: null,
      error: null,
    }
  },
  computed: {
    ...mapGetters({
      getAllClientGroups: 'auth/getAllClientGroups',
      getGroup: 'auth/getGroup',
      isLoadingOverviewData: 'client/isLoadingOverviewData',
      firstAvailableClient: 'auth/firstAvailableClient',
    }),
    isGroupSet() {
      return (
        this.selectedGroup &&
        (this.selectedGroup > 0 || this.selectedGroup === -1)
      )
    },
    selectedGroupDetails() {
      return this.getGroup(this.selectedGroupId)
    },
    allClientsFromSelectedGroup() {
      return this.selectedGroupDetails?.clients || []
    },
    hasOverviewData() {
      return isNonEmptyArray(this.overviewData)
    },
    filteredOverviewData() {
      if (!this.hasOverviewData) return []
      return this.overviewData.filter((dataItem) =>
        this.selectedSchools.includes(dataItem.locationId)
      )
    },
    hasFailedToLoad() {
      return !!this.error
    },
  },
  watch: {
    filterDate: {
      handler: throttle(async function() {
        await this.loadOverviewData(this.selectedGroupId, this.filterDate)
      }, 500),
    },
  },
  async mounted() {
    this.selectedGroupId = this.getAllClientGroups[0].groupId
    this.filterDate = dayjs().format('YYYY-MM-DD')
  },
  methods: {
    ...mapActions({
      loadClientGroupOverviewSchoolStatus:
        'client/loadClientGroupOverviewSchoolStatus',
      setClient: 'client/setClient',
    }),
    initSchoolsFilter() {
      this.updateSchoolFilter(
        this.overviewData.map((dataItem) => dataItem.locationId)
      )
    },
    async selectClient(clientId) {
      const result = await this.setClient(clientId)
      if (result.isSuccess)
        this.$router.push({ name: routeDefinitions.home.name })
    },
    async changeClientGroup(selectedGroup) {
      this.selectedGroupId = selectedGroup
      this.updateSchoolFilter([]) // HACK: Ensures map markers are cleared when changing groups
      await this.loadOverviewData(this.selectedGroupId, this.filterDate)
    },
    updateSchoolFilter(selectedSchoolsList) {
      this.selectedSchools = selectedSchoolsList
    },
    /**
     * @param {String} dateString Date string formatted as YYYY-MM-DD
     */
    handleDateUpdate(dateString) {
      this.filterDate = dateString
    },
    handleDateUpdateFromCalendar(value) {
      this.filterDate = value
    },
    async loadOverviewData(clientGroupId, filterDate) {
      this.error = null
      if (!dayjs(filterDate).isValid()) return

      const result = await this.loadClientGroupOverviewSchoolStatus({
        clientGroupId,
        filterDate,
      })

      if (!result.isSuccess) {
        this.error = result.error
        return
      }

      this.overviewData = result.data

      // Select all schools by default
      this.initSchoolsFilter()
    },
    async focusOnSchool(locationId) {
      this.schoolToFocusOn = -1 // HACK: Forces watcher on map to trigger
      await this.$nextTick()
      this.schoolToFocusOn = locationId
    },
    async retryLoad() {
      await this.loadOverviewData(this.selectedGroupId, this.filterDate)
    },
    async returnToFirstClient() {
      const client = this.firstAvailableClient
      return await this.selectClient(client?.clientId)
    },
  },
}
</script>

<template>
  <section>
    <header
      :class="[
        isMediumOrHigher
          ? 'd-flex flex-rows justify-space-between align-center'
          : '',
      ]"
    >
      <div>
        <ClientGroupOverviewGroupSelector
          :groups="getAllClientGroups"
          :loading="isLoadingOverviewData"
          :selected-group="selectedGroupId"
          @updateGroup="changeClientGroup"
        />
        <div :class="[isMediumOrHigher ? 'mt-12 mb-12 d-flex' : 'mt-6 mb-6']">
          <ClientGroupOverviewSchoolMultiSelector
            :loading="isLoadingOverviewData"
            :disabled="hasFailedToLoad"
            :schools="overviewData"
            :selected-schools="selectedSchools"
            @updated="updateSchoolFilter"
          />

          <ClientGroupOverviewDateFilter
            :filter-date="filterDate"
            :loading="isLoadingOverviewData"
            @updatedInput="handleDateUpdate"
            @updatedCalendar="handleDateUpdateFromCalendar"
          />
        </div>
      </div>
      <div>
        <ClientGroupOverviewStatisticsContainer
          :overview-data="filteredOverviewData"
        />
      </div>
    </header>

    <SkeletonClientGroupOverviewContainer v-if="isLoadingOverviewData" />
    <ClientGroupOverviewFailureBlock
      v-else-if="hasFailedToLoad"
      :error="error"
      @back="returnToFirstClient"
      @retry="retryLoad"
    />
    <div
      v-else-if="hasOverviewData"
      :class="[isMediumOrHigher ? 'mt-12 d-flex justify-space-between' : '']"
    >
      <ClientGroupOverviewSchoolsMap
        v-if="isMediumOrHigher"
        :style="{ width: isMediumOrHigher ? '40%' : '100%' }"
        :class="[isMediumOrHigher ? 'mr-4' : 'mb-4']"
        :schools="filteredOverviewData"
        :focus-on-school="schoolToFocusOn"
      />

      <ClientGroupOverviewSchoolsTable
        :style="{ width: isMediumOrHigher ? '60%' : '100%' }"
        :class="[isMediumOrHigher ? 'ml-4' : 'mt-8']"
        :schools="filteredOverviewData"
        @selectClient="selectClient"
        @rowClicked="focusOnSchool"
      />
    </div>
  </section>
</template>
